import { useRef, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faFolder, faTimes, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import uploadIcon from "../assets/icons/upload.svg";
import message from "../utils/message";

function formatFileSize(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

async function scanFiles(items) {
    const fileList = [];

    async function readEntry(entry, path = "") {
        if (entry.isFile) {
            return new Promise((resolve) => {
                entry.file((file) => {
                    const relativePath = path ? `${path}/${file.name}` : file.name;
                    try {
                        Object.defineProperty(file, 'webkitRelativePath', {
                            value: relativePath,
                            writable: true,
                            configurable: true
                        });
                    } catch (e) {
                        file._relativePath = relativePath;
                    }
                    fileList.push(file);
                    resolve();
                }, () => resolve());
            });
        } else if (entry.isDirectory) {
            const dirReader = entry.createReader();
            const currentPath = path ? `${path}/${entry.name}` : entry.name;

            const readEntries = () => {
                return new Promise((resolve) => {
                    dirReader.readEntries(async (entries) => {
                        if (!entries || entries.length === 0) {
                            resolve();
                        } else {
                            for (const childEntry of entries) {
                                await readEntry(childEntry, currentPath);
                            }
                            await readEntries();
                            resolve();
                        }
                    }, () => resolve());
                });
            };
            await readEntries();
        }
    }

    const entries = [];
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.webkitGetAsEntry) {
            const entry = item.webkitGetAsEntry();
            if (entry) entries.push(entry);
        }
    }

    for (const entry of entries) {
        await readEntry(entry);
    }

    return fileList;
}

/**
 * Upload Modal — combined file and folder uploader with drag & drop.
 *
 * Props:
 *   upload(file, action, tagIds)  – upload function for loose files
 *   uploadFolder(files)           – upload function for folder hierarchies
 *   checkExists(file)             – duplicate check
 *   setIsVisible(bool)            – close modal
 */
function UploadModal({ setIsVisible, upload, checkExists, uploadFolder }) {
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);

    async function handleFileDrop(e) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const items = e.dataTransfer?.items;
        if (items && items.length > 0) {
            const files = await scanFiles(items);
            if (files.length > 0) {
                setPendingFiles(prev => [...prev, ...files]);
                return;
            }
        }

        const files = Array.from(e.dataTransfer?.files || []);
        if (files.length > 0) {
            setPendingFiles(prev => [...prev, ...files]);
        }
    }

    function handleFileChange(e) {
        const { files } = e.target;
        if (files && files.length > 0) {
            setPendingFiles(prev => [...prev, ...Array.from(files)]);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    }

    function handleFolderChange(e) {
        const { files } = e.target;
        if (files && files.length > 0) {
            setPendingFiles(prev => [...prev, ...Array.from(files)]);
            if (folderInputRef.current) {
                folderInputRef.current.value = "";
            }
        }
    }

    function removeFile(index) {
        setPendingFiles(prev => prev.filter((_, i) => i !== index));
    }

    function clearAll() {
        setPendingFiles([]);
    }

    async function handleConfirmUpload() {
        if (pendingFiles.length === 0 || uploading) return;

        const folderFiles = pendingFiles.filter(f => {
            const rel = f.webkitRelativePath || f._relativePath || "";
            return rel.includes('/');
        });
        const looseFiles = pendingFiles.filter(f => {
            const rel = f.webkitRelativePath || f._relativePath || "";
            return !rel.includes('/');
        });

        // Validate folder constraints if folder files present
        if (folderFiles.length > 0) {
            const maxDepth = 3;
            const folderDepths = folderFiles.map((file) => {
                const relativePath = (file.webkitRelativePath || file._relativePath || file.name).replace(/\\/g, '/');
                const parts = relativePath.split('/');
                return parts.length > 1 ? parts.length - 1 : 1;
            });
            const maxDepthFound = Math.max(...folderDepths, 0);
            if (maxDepthFound > maxDepth) {
                message("error", "Folder depth exceeds maximum of 3 layers");
                return;
            }

            const maxSize = 200 * 1024 * 1024;
            const totalSize = folderFiles.reduce((acc, file) => acc + file.size, 0);
            if (totalSize > maxSize) {
                message("error", "Total folder size exceeds maximum of 200MB");
                return;
            }
        }

        setUploading(true);
        try {
            // Upload folder structure
            if (folderFiles.length > 0 && uploadFolder) {
                await uploadFolder(folderFiles);
            }

            // Upload individual files
            if (looseFiles.length > 0 && upload) {
                for (const file of looseFiles) {
                    const exists = await checkExists(file);
                    if (!exists) {
                        upload(file);
                    }
                }
            }

            setIsVisible(false);
        } catch (err) {
            console.error("Upload error:", err);
            message("error", "Upload failed: " + (err.message || err));
        } finally {
            setUploading(false);
        }
    }

    return (
        <div
            style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
            id="select-modal"
            tabIndex="-1"
            aria-hidden="true"
            className="flex overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full"
        >
            <div className="relative p-4 w-full max-w-lg max-h-full animate__animated animate__fadeIn animate__faster">
                <div className="relative bg-white rounded-xl shadow-2xl pb-4 border border-gray-100">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t">
                        <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-gray-800">Upload Files & Folders</h3>
                        </div>
                        <button
                            onClick={() => setIsVisible(false)}
                            type="button"
                            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm h-8 w-8 ms-auto inline-flex justify-center items-center"
                        >
                            <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                            </svg>
                            <span className="sr-only">Close modal</span>
                        </button>
                    </div>

                    {/* Drop Zone */}
                    <div
                        onDrop={handleFileDrop}
                        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                        className={`w-11/12 h-36 mx-auto mt-4 rounded-xl border-2 border-dashed flex flex-col items-center justify-center transition-colors duration-200 ${
                            isDragging ? 'bg-blue-50 border-blue-500 scale-[1.01]' : 'bg-slate-50 border-blue-300'
                        }`}
                    >
                        <img src={uploadIcon} alt="Upload" className="w-9 h-9 opacity-80" />
                        <div className="text-gray-600 text-xs font-medium text-center mt-2.5">
                            Drag & Drop your <span className="font-semibold text-blue-600">files</span> or <span className="font-semibold text-blue-600">folders</span> here
                        </div>
                        <div className="text-gray-400 text-[11px] mt-0.5">
                            Folder depth up to 3 layers, max 200MB
                        </div>
                    </div>

                    <div className="text-gray-400 text-xs font-medium text-center mt-3">Or choose from your computer</div>

                    {/* Hidden Inputs */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        multiple
                        onChange={handleFileChange}
                    />
                    <input
                        type="file"
                        ref={folderInputRef}
                        webkitdirectory="true"
                        directory="true"
                        multiple
                        className="hidden"
                        onChange={handleFolderChange}
                    />

                    {/* Choose Buttons */}
                    <div className="flex justify-center gap-3 mt-2.5">
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="px-3.5 py-1.5 bg-blue-500 hover:bg-blue-600 rounded-lg text-white text-xs font-medium transition duration-200 flex items-center gap-1.5 shadow-sm"
                        >
                            <FontAwesomeIcon icon={faFile} /> Choose Files
                        </button>
                        <button
                            type="button"
                            onClick={() => folderInputRef.current?.click()}
                            className="px-3.5 py-1.5 bg-zinc-700 hover:bg-zinc-800 rounded-lg text-white text-xs font-medium transition duration-200 flex items-center gap-1.5 shadow-sm"
                        >
                            <FontAwesomeIcon icon={faFolder} /> Choose Folder
                        </button>
                    </div>

                    {/* Pending Files List */}
                    {pendingFiles.length > 0 && (
                        <div className="mx-5 mt-4">
                            <div className="flex items-center justify-between text-xs font-semibold text-gray-600 mb-2">
                                <span>Selected items ({pendingFiles.length})</span>
                                <button
                                    type="button"
                                    onClick={clearAll}
                                    className="text-xs text-red-500 hover:text-red-700 font-normal hover:underline"
                                >
                                    Clear all
                                </button>
                            </div>
                            <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 bg-gray-50/50">
                                {pendingFiles.map((file, i) => {
                                    const displayPath = file.webkitRelativePath || file._relativePath || file.name;
                                    const isFromFolder = displayPath.includes('/');
                                    return (
                                        <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs text-gray-700 hover:bg-white transition-colors">
                                            <div className="flex items-center gap-2 truncate flex-1 mr-2">
                                                <FontAwesomeIcon
                                                    icon={isFromFolder ? faFolderOpen : faFile}
                                                    className={`text-xs ${isFromFolder ? 'text-amber-500' : 'text-blue-500'}`}
                                                />
                                                <span className="truncate" title={displayPath}>
                                                    {displayPath}
                                                </span>
                                            </div>
                                            <span className="text-[10px] text-gray-400 mr-2 flex-shrink-0">
                                                {formatFileSize(file.size)}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => removeFile(i)}
                                                className="text-gray-400 hover:text-red-500 flex-shrink-0"
                                            >
                                                <FontAwesomeIcon icon={faTimes} size="xs" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="flex justify-end gap-2 mx-5 mt-4 pt-3 border-t">
                        <button
                            type="button"
                            onClick={() => setIsVisible(false)}
                            className="px-4 py-2 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition duration-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirmUpload}
                            disabled={pendingFiles.length === 0 || uploading}
                            className="px-4 py-2 text-xs text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition duration-200 disabled:opacity-40 disabled:cursor-not-allowed font-medium shadow-sm"
                        >
                            {uploading ? "Uploading..." : `Upload (${pendingFiles.length})`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UploadModal;
